import { useCallback, useEffect, useState } from 'react';
import { GetAdminChangelogEditionsAPI } from '../../../../api/AdminAPI';

// Enquanto um envio está saindo, o progresso é atualizado sozinho
const POLL_INTERVAL_MS = 15000;

/**
 * useEditions
 *
 * Envios do e-mail de novidades (admin/changelog/editions.php) e destinatários estimados.
 * A API garante um envio em Rascunho: o "Próximo envio" é o rascunho mais recente.
 *
 * @returns {{ nextEdition: Object|null, otherEditions: Array, recipientsEstimate: number|null,
 *   isLoading: boolean, error: string|null, refresh: Function, reloadSilently: Function }}
 */
function useEditions() {
  const [editions, setEditions] = useState([]);
  const [recipientsEstimate, setRecipientsEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // silent: atualização do progresso, sem o carregando da tela
  const load = useCallback((silent = false) => {
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    GetAdminChangelogEditionsAPI((response) => {
      if (!response || response.success === false) {
        if (!silent) setError(response?.error || 'Erro ao carregar os envios. Tente novamente.');
        setIsLoading(false);
        return;
      }
      const list = Array.isArray(response.editions) ? response.editions : [];
      setEditions([...list].sort((a, b) => (b.number || 0) - (a.number || 0)));
      setRecipientsEstimate(response.recipients_estimate ?? null);
      setError(null);
      setIsLoading(false);
    });
  }, []);

  const refresh = useCallback(() => load(false), [load]);
  const reloadSilently = useCallback(() => load(true), [load]);

  useEffect(() => { load(false); }, [load]);

  const hasActive = editions.some((edition) => edition.status === 'sending' || (edition.sends?.pending || 0) > 0);

  useEffect(() => {
    if (!hasActive) return undefined;
    const timer = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasActive, load]);

  const nextEdition = editions.find((edition) => edition.status === 'draft') || null;
  const otherEditions = editions.filter((edition) => edition !== nextEdition);

  return { nextEdition, otherEditions, recipientsEstimate, isLoading, error, refresh, reloadSilently };
}

export default useEditions;
